import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Button, Select, Tag, Space, message, Row, Col, Card, Statistic, Input, Modal,
} from 'antd';
import { TeamOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { user_api } from '../api/user-api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', role: undefined, status: undefined });

  const load = async () => {
    try {
      setLoading(true);
      const [listRes, statRes] = await Promise.all([
        user_api.getAllUsers({
          limit: 200,
          search: filters.search?.trim() || undefined,
          role: filters.role,
          status: filters.status,
        }),
        user_api.getStatistics(),
      ]);
      if (listRes.success) setUsers(listRes.data || []);
      if (statRes.success) setStats(statRes.data || {});
    } catch (error) {
      message.error(error.message || 'Không tải được users (cần quyền admin)');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.role, filters.status]);

  const overview = stats.overview || stats;

  const columns = useMemo(() => [
    { title: 'Email', dataIndex: 'email', ellipsis: true },
    { title: 'Tên', dataIndex: 'displayName', width: 160 },
    {
      title: 'Role',
      dataIndex: 'role',
      width: 140,
      render: (role, row) => (
        <Select
          size="small"
          value={role}
          style={{ width: 110 }}
          options={[
            { value: 'admin', label: 'admin' },
            { value: 'user', label: 'user' },
            { value: 'viewer', label: 'viewer' },
          ]}
          onChange={async (value) => {
            const res = await user_api.updateUserRole(row.id, value);
            if (res.success) {
              message.success('Đã đổi role');
              load();
            } else {
              message.error(res.message);
            }
          }}
        />
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 150,
      render: (status, row) => (
        <Select
          size="small"
          value={status}
          style={{ width: 120 }}
          options={[
            { value: 'active', label: 'active' },
            { value: 'inactive', label: 'inactive' },
            { value: 'suspended', label: 'suspended' },
          ]}
          onChange={async (value) => {
            const res = await user_api.updateUserStatus(row.id, value);
            if (res.success) {
              message.success('Đã đổi trạng thái');
              load();
            } else {
              message.error(res.message);
            }
          }}
        />
      ),
    },
    {
      title: 'FCM',
      width: 70,
      render: (_, row) => (row.fcmTokens?.length || 0),
    },
    {
      title: 'Đăng nhập',
      width: 170,
      render: (_, row) => (row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString('vi-VN') : '—'),
    },
    {
      title: '',
      width: 80,
      render: (_, row) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => {
            Modal.confirm({
              title: `Xóa ${row.email}?`,
              okType: 'danger',
              onOk: async () => {
                const res = await user_api.deleteUser(row.id);
                if (res.success) {
                  message.success('Đã xóa user');
                  load();
                } else {
                  message.error(res.message);
                }
              },
            });
          }}
        />
      ),
    },
  ], []);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Người dùng</h1>
        <Space wrap>
          <Input
            allowClear
            placeholder="Tìm email / tên"
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            onPressEnter={load}
          />
          <Select
            allowClear
            placeholder="Role"
            style={{ width: 120 }}
            value={filters.role}
            onChange={(role) => setFilters((prev) => ({ ...prev, role }))}
            options={[
              { value: 'admin', label: 'admin' },
              { value: 'user', label: 'user' },
              { value: 'viewer', label: 'viewer' },
            ]}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 130 }}
            value={filters.status}
            onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
            options={[
              { value: 'active', label: 'active' },
              { value: 'inactive', label: 'inactive' },
              { value: 'suspended', label: 'suspended' },
            ]}
          />
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} lg={6}><Card><Statistic title="Tổng" value={overview.total || users.length} prefix={<TeamOutlined />} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="Active" value={overview.byStatus?.active || overview.active || 0} valueStyle={{ color: '#3f8600' }} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="Admin" value={overview.byRole?.admin || overview.admin || 0} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="User" value={overview.byRole?.user || overview.user || 0} /></Card></Col>
      </Row>

      <Table
        rowKey={(row) => row.id || row._id}
        loading={loading}
        columns={columns}
        dataSource={users}
        pagination={{ pageSize: 12, showTotal: (n) => `${n} users` }}
      />
    </div>
  );
}
